/**
 * Mirrors the dict shape returned by /api/spreads (see src/dashboard/app.py).
 * Only fields actually used by the UI are declared; extras are tolerated.
 */
export type Tier = "vh" | "h" | "med" | "low";

/** Contract-verification status between the exchange-reported address and
 *  the canonical ``dexs[NET].contract_address``. "unknown" when either side
 *  doesn't expose it (most common: the exchange API doesn't report the
 *  address). Used by the UI to overlay a small confirmation indicator on
 *  each network chip. */
export type ContractMatch = "ok" | "mismatch" | "unknown";

export interface NetworkInfo {
  n: string;
  wd: boolean;
  dep: boolean;
  buy_dep?: boolean;
  sell_wd?: boolean;
  fee?: number | null;
  /** Reported by the buy-side exchange (CEX-CEX only emits this when buy
   *  is a CEX; for DEX-CEX the DEX side is implicit). */
  buy_c?: string;
  /** Reported by the sell-side exchange (same caveat). */
  sell_c?: string;
  /** Canonical contract from ``asset.dexs[NET].contract_address``. */
  canon_c?: string;
  buy_match?: ContractMatch;
  sell_match?: ContractMatch;
}

/** Per-side network entry as emitted by the backend in
 *  buy_networks / sell_networks: raw deposit/withdraw flags from the
 *  exchange's networks list. DEX legs synthesize a single entry where
 *  d=w=true (wallet has no rail to gate). ``d``/``w`` are tri-state:
 *  true/false when the exchange exposes a status, null when the chain
 *  came from canonical/contract sources without an explicit rail flag
 *  (UI renders ❔ / gray instead of faking a closed rail). */
export interface SideNetwork {
  n: string;
  d: boolean | null;
  w: boolean | null;
  /** Withdrawal fee (in base-asset units) for this chain on this
   *  exchange — carried from ``mapping_human.withdrawal.fee`` so the
   *  UI can render the cost of moving tokens off this leg without a
   *  second lookup. */
  f?: number;
  /** Exchange-reported contract (from cexs[ex].networks[NET]). Missing
   *  when the exchange API doesn't surface it. */
  c?: string;
  /** Canonical contract (copied from dexs[NET]) for same network. */
  canon_c?: string;
  match?: ContractMatch;
}

export interface Spread {
  asset: string;
  symbol: string;

  buy_exchange: string;
  buy_market: "spot" | "futures";
  buy_price: number;
  buy_url?: string | null;
  buy_ticker?: string;
  buy_fee_pct?: number;

  sell_exchange: string;
  sell_market: "spot" | "futures";
  sell_price: number;
  sell_url?: string | null;
  sell_ticker?: string;
  sell_fee_pct?: number;

  /** Top-of-book: best ask (buy) / best bid (sell), same units as
   *  buy_price / sell_price (which are the depth-weighted VWAP). The
   *  PRICE column renders these so the figure ticks with every top-of-
   *  book move. Missing on premarket / older snapshots, UI falls back
   *  to the VWAP price. */
  top_buy_price?: number;
  top_sell_price?: number;

  volume_usd: number;
  volume_tokens?: number;
  // Per-leg 24h USD volume from the analyzer's view of the mapping
  // (added 2026-05-28). Independent of ``volume_usd`` which is the
  // recommended notional for THIS arb trade. Used by the table's
  // dedicated "Vol 24h" column for venue-liquidity context.
  buy_vol_24h_usd?: number | null;
  sell_vol_24h_usd?: number | null;
  gross_spread_pct?: number;
  net_spread_pct: number;
  net_profit_usd: number;

  first_seen_at: number;
  stale: boolean;
  tier?: Tier;

  common_networks?: NetworkInfo[];
  buy_networks?: SideNetwork[];
  sell_networks?: SideNetwork[];

  funding_buy?: number | null;
  funding_sell?: number | null;

  /** TradFi instrument group key — present only on rows sourced from
   *  ``spread:tradfi_opportunities`` (CL/OIL, HK50/HSI, JP225/NK225,
   *  SPX500, etc.). Backend's ``_opp_to_dict`` (engine.py) emits this
   *  when ``opp.tradfi_group`` is set, populated for derivative-on-
   *  index contracts whose underlying isn't crypto. The /spreads
   *  page uses it to gate the ``TradFi`` toggle in FilterBar — OFF
   *  hides these rows from the default crypto-only view, ON keeps
   *  only these. */
  tradfi_group?: string;

  /** Asset class of the underlying (#58): "equity" | "commodity" | "forex".
   *  Emitted by backend ``_opp_to_dict`` only when NOT crypto, so a missing
   *  value means crypto. Drives the asset-class filter chips in FilterBar. */
  asset_class?: "crypto" | "equity" | "commodity" | "forex";
}

export interface SpreadGroup {
  symbol: string;
  tier: Tier;
  routes: Spread[];
}

export function spreadUid(s: Spread): string {
  return `${s.asset}|${s.buy_exchange}:${s.buy_market}|${s.sell_exchange}:${s.sell_market}`;
}
